// @flow

import React, { Component } from "react";
import styled from "styled-components";
import AnimateHeight from "react-animate-height";

import { colors } from "../styles";
import A from "../A";

function humanFriendlyNumber(number: number): string {
  if (number < 1000) {
    return `$${Math.round(number)}`;
  }
  if (number < 1000000) {
    return `$${Math.round(number / 1000)}k`;
  }
  return `$${(number / 1000000).toFixed(2)}M`;
}

const TIMELINE_HEIGHT = 400;
type Milestone = {
  title: string,
  description: string | React.Element<*>,
  value: number => number
};

const milestones: Array<Milestone> = [
  {
    title: "Personal Capital Call",
    description: (
      <span>
        If you've opened a{" "}
        <A href="https://www.personalcapital.com/">
          Personal Capital
        </A>{" "}
        account, expect a phone call promoting their paid advisory service once
        you hit a net worth of $100k. You don't have to sign up for it, but it's
        nice to get the call and know you're making progress!
      </span>
    ),
    value: annualExpenses => 100000
  },
  {
    title: "On Journey",
    description:
      "You're on your way! You're here once your total net worth is positive.",
    value: annualExpenses => 0
  },
  {
    title: '"FU" Money',
    description:
      "You've got \"FU money\" once you feel comfortable leaving your job, if neccessary, with the full confidence that you've got the resources to land on your feet. This is a highly personal decision, of course, but for this calculator we'll define it as one year of living expenses.",
    value: annualExpenses => annualExpenses
  },
  {
    title: "Half FI",
    description: (
      <span>
        Congrats! You're halfway to financial independence money-wise. But now
        that you have a nice-sized nest egg working for you, you're actually
        <em> more</em> than 50% of the way done time-wise. Hang in there — not
        too long left!
      </span>
    ),
    value: annualExpenses => annualExpenses * 12.5
  },
  {
    title: "Skinny FI",
    description:
      "Skinny FI happens when you've reached 20x your annual expenses. Some people consider this a \"safe\" time to quit the day job and start a side hustle. As long as you're willing and able to bring in a little bit of income going forward, this may be a good time to start something new.",
    value: annualExpenses => annualExpenses * 20
  },
  {
    title: "Official FI",
    description: (
      <span>
        Congratulations! If you follow the{" "}
        <A href="http://www.mrmoneymustache.com/2012/05/29/how-much-do-i-need-for-retirement/">
          4% rule
        </A>{" "}
        you're officially financially independent! Enjoy the rest of your life;
        you deserve it. :)
      </span>
    ),
    value: annualExpenses => annualExpenses * 25
  },
  {
    title: "Fat FI",
    description:
      "Once you've reached a net worth of 33x your annual expenses, your financial independence is as much of a \"sure thing\" as anything can be in this life! That puts you at a 3.33% withdrawal rate, which should sustain your nest egg indefinitely. Congratulations, you're there!",
    value: annualExpenses => annualExpenses * 33
  }
];

type TimelineProps = {
  netWorth: number,
  annualExpenses: number
};

export default class Timeline extends Component {
  state = {
    // Index of the currently selected milestone.
    selectedMilestone: 1
  };
  props: TimelineProps;

  componentWillReceiveProps(newProps: TimelineProps) {
    // Set the currently selected milestone as the biggest one that we've achieved.
    let newMilestone = [0, 0];
    milestones.forEach((m, i) => {
      const mValue = m.value(newProps.annualExpenses);
      if (mValue > newMilestone[1] && mValue <= newProps.netWorth) {
        newMilestone = [i, mValue];
      }
    });
    if (newMilestone[1] !== this.state.selectedMilestone) {
      this.setState({ selectedMilestone: newMilestone[0] });
    }
  }

  _getPosition(dollarAmount: number) {
    // The largest possible value on the timeline.
    const timelineMax = Math.max(this.props.annualExpenses * 33, 100000);

    const unadjustedValue = dollarAmount / timelineMax * TIMELINE_HEIGHT;

    // Clamp between 0 and TIMELINE_HEIGHT to always stay on the timeline
    const clampedValue = Math.max(
      Math.min(unadjustedValue, TIMELINE_HEIGHT),
      0
    );

    // Squash to leave the top/bottom 5px of the timeline unoccupied
    return clampedValue * (TIMELINE_HEIGHT - 10) / TIMELINE_HEIGHT + 5;
  }

  render() {
    const { annualExpenses, netWorth } = this.props;
    const selectedMilestone = milestones[this.state.selectedMilestone];

    return (
      <TimelineContainer>
        <TimelineSide narrow>
          {milestones.map((m, i) => {
            const value = m.value(annualExpenses);
            const completed = netWorth >= value;
            return (
              <MilestoneContainer
                key={m.title}
                style={{ top: `${this._getPosition(value)}px` }}
                onClick={() => this.setState({ selectedMilestone: i })}
              >
                <MilestoneTitle
                  key={m.title}
                  completed={completed}
                  selected={i === this.state.selectedMilestone}
                >
                  {completed &&
                    <span className="checkmark">&#10003;&nbsp;</span>}
                  {m.title}
                </MilestoneTitle>
                <div className="indicator" />
              </MilestoneContainer>
            );
          })}
        </TimelineSide>
        <TimelineBar>
          <AnimateHeight
            duration={1000}
            height={this._getPosition(netWorth) + 1}
          >
            <TimelineFill style={{ height: `500px` }} />
          </AnimateHeight>
        </TimelineBar>
        <TimelineSide>
          <MilestonePointer
            style={{
              height: `${this._getPosition(
                selectedMilestone.value(annualExpenses)
              )}px`
            }}
          />
          <MilestoneDescription>
            <div className="title">
              {selectedMilestone.title}{" "}
            </div>
            <div className="price">
              {humanFriendlyNumber(selectedMilestone.value(annualExpenses))}
            </div>
            <div className="description">
              {selectedMilestone.description}
            </div>
          </MilestoneDescription>
        </TimelineSide>
      </TimelineContainer>
    );
  }
}

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: ${TIMELINE_HEIGHT}px;
  align-self: stretch;
`;

const TimelineBar = styled.div`
  width: 10px;
  border-radius: 5px;
  height: 100%;
  background-color: ${colors.grey};
`;

const TimelineFill = styled.div`
border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  background-color: ${colors.green};
`;

const TimelineSide = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: row;
`;

const MilestoneContainer = styled.div`
  position: absolute;
  right: 0;
  display: flex;
  justify-content: flex-end;
  cursor: pointer;
  margin-top: -0.4em;
  font-size: 0.8em;

  .indicator {
    background: #ccc;
    width: 5px;
    height: 1px;
    z-index: 1;
    margin-top: 0.4em;
  }
`;

const MilestoneTitle = styled.div`
  text-transform: uppercase;
  text-align: right;
  margin-right: 3px;
  line-height: 1em;

  background-color: white;
  color: ${props => (props.completed ? colors.green : "inherit")};
  font-weight: ${props => (props.selected ? "bold" : "inherit")};
  z-index: ${props => (props.selected ? "1" : "inherit")};

  .checkmark { font-weight: 300; }
`;

const pointerColor = "#333";
const MilestoneDescription = styled.div`
  border-top: 1px solid ${pointerColor};
  padding-left: 10px;
  padding-top: 5px;
  flex: 1;

  .price {
    font-style: italic;
    margin-bottom: 10px;
  }

  .title {
    font-weight: bold;
  }

  .description {
    font-size: 0.8em;
    line-height: 1.5em;
  }
`;
const MilestonePointer = styled.div`
  border-bottom: 1px solid ${pointerColor};
  border-right: 1px solid ${pointerColor};
  width: 10px;
`;



// WEBPACK FOOTER //
// ./src/milestones/Timeline.js